import { useT } from '@/lib/i18n';
import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'pf-install-dismissed';

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  // iPad Safari masquerades as Mac on iOS 13+, but has maxTouchPoints > 1
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' &&
      (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints !== undefined &&
      ((navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints ?? 0) > 1)
  );
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  if ((window.navigator as Navigator & { standalone?: boolean }).standalone) return true;
  return false;
}

/**
 * "📲 Installer" button. Captures the `beforeinstallprompt` event globally and
 * surfaces a prompt. On iOS where the event isn't fired, shows a one-shot
 * hint card explaining the Partager → Sur l'écran d'accueil flow.
 */
export function InstallButton() {
  const { t } = useT();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    if (isStandalone()) return;

    const onBefore = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onBefore);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBefore);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (installed) return null;

  // iOS path
  if (isIOS()) {
    const dismissed =
      typeof localStorage !== 'undefined' && localStorage.getItem(DISMISS_KEY) === '1';
    if (dismissed && !showIosHint) return null;
    return (
      <>
        <button
          type="button"
          onClick={() => setShowIosHint(true)}
          className="px-4 py-2 min-h-[40px] bg-white text-ink font-hand text-[14px] border-2 border-ink rounded-control shadow-ink-sm hover:translate-y-[-1px] transition-transform"
          data-testid="install-button"
        >
          {t('install.cta')}
        </button>
        {showIosHint ? (
          <div
            className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4"
            // biome-ignore lint/a11y/useSemanticElements: native <dialog> doesn't compose with our backdrop-click pattern
            role="dialog"
            aria-modal="true"
            aria-label="Installer PharmaFiche sur iPhone/iPad"
            onClick={() => setShowIosHint(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setShowIosHint(false);
            }}
          >
            <div
              className="bg-paper border-[2.5px] border-ink rounded-card p-5 max-w-md font-hand text-[15px] text-ink"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <h2 className="font-gloock text-[20px] mb-2">Installer sur iOS</h2>
              <ol className="list-decimal pl-5 space-y-1">
                <li>
                  Appuie sur le bouton <span aria-hidden>⬆️</span> Partager dans la barre Safari
                </li>
                <li>Choisis « Sur l'écran d'accueil »</li>
                <li>Valide « Ajouter » en haut à droite</li>
              </ol>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    localStorage.setItem(DISMISS_KEY, '1');
                    setShowIosHint(false);
                  }}
                  className="px-3 py-1 text-ink-soft underline-offset-2 hover:underline"
                >
                  Ne plus afficher
                </button>
                <button
                  type="button"
                  onClick={() => setShowIosHint(false)}
                  className="px-3 py-1 bg-accent-blue text-white border-2 border-ink rounded-control shadow-ink-sm"
                >
                  J'ai compris
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </>
    );
  }

  // Standard PWA path — only render the button when the event is captured
  if (!deferred) return null;

  return (
    <button
      type="button"
      onClick={async () => {
        await deferred.prompt();
        const { outcome } = await deferred.userChoice;
        if (outcome === 'accepted') setDeferred(null);
      }}
      className="px-4 py-2 min-h-[40px] bg-accent-yellow text-ink font-hand text-[14px] border-2 border-ink rounded-control shadow-ink-sm hover:translate-y-[-1px] transition-transform"
      data-testid="install-button"
    >
      {t('install.cta')}
    </button>
  );
}
